// Etiquetas del módulo de bodega. Las claves son las de inventories/enums.py,
// slots/enums.py y warehouses/enums.py en el backend; si allá se agrega un
// valor nuevo, se agrega acá.
//
// Este archivo es la ÚNICA fuente del español que ve el cliente. El backend
// rotula sus enums en inglés porque esas etiquetas se leen en el admin de
// Django, que es herramienta interna. Por eso no se usan los campos
// `*_display` que devuelve la API: traen el texto en inglés.
//
// Vive en utils y no dentro de una pantalla a propósito: el rediseño del front
// va a rehacer el JSX, pero estos nombres son los del negocio y sobreviven.

export const STOCK_STATE_LABELS = {
  available: "Disponible",
  reserved: "Reservado",
  picked: "En preparación",
  quarantine: "Cuarentena",
  damaged: "Dañado",
  in_transit: "En tránsito",
};

// Cómo se pinta cada estado. `default` es gris: sin color, sin alarma.
export const STOCK_STATE_COLORS = {
  available: "success",
  reserved: "warning",
  picked: "info",
  quarantine: "warning",
  damaged: "error",
  in_transit: "default",
};

export const MOVEMENT_TYPE_LABELS = {
  receipt: "Recepción",
  initial_load: "Carga inicial",
  putaway: "Guardado",
  transfer: "Traslado",
  reserve: "Reserva",
  release: "Liberación de reserva",
  pick: "Picking",
  pick_reversal: "Reversa de picking",
  ship: "Despacho",
  adjustment: "Ajuste",
};

export const WAREHOUSE_KIND_LABELS = {
  seller: "Bodega del cliente",
  distribution_center: "Centro de distribución",
};

export const ZONE_TYPE_LABELS = {
  receiving: "Recepción",
  storage: "Almacenaje",
  picking: "Picking",
  shipping: "Despacho",
  quarantine: "Cuarentena",
};

// Cómo se comporta un kit. El armado lo hace el operario en la estación Kits, y en el kardex
// queda como ajuste con referencia «kit:<id>».
export const KIT_MODE_LABELS = {
  virtual: "Virtual — se explota en sus componentes al vender",
  assembled: "Armado — tiene stock propio, se ensambla en bodega",
};

// Los dos orígenes de la pantalla de Almacenado.
export const PUTAWAY_SOURCE_LABELS = {
  receiving: "Desde recepción",
  restock: "Por reponer",
  history: "Historial",
};

export const RECEIPT_STATUS_LABELS = {
  pending: "Pendiente",
  partial: "Parcial",
  received: "Recibida",
  cancelled: "Anulada",
};

export const RECEIPT_EVENT_LABELS = {
  created: "Creada",
  updated: "Editada",
  received: "Recepción",
  closed: "Cerrada",
  cancelled: "Anulada",
};

export const DISCREPANCY_REASON_LABELS = {
  not_found: "No estaba en la ubicación",
  less_than_expected: "Había menos de lo esperado",
  damaged: "Dañado",
  wrong_location: "Estaba en otra ubicación",
};

// El estado de una orden dentro de la bodega. Las claves son las de
// orders/wms_states.py. No existe como columna en ninguna tabla: el servidor lo
// calcula de las reservas, lo preparado y los bultos cada vez que se pregunta.
export const WMS_ORDER_STATE_LABELS = {
  entered: "Ingresada",
  reserved: "Reservada",
  picking: "En picking",
  picked: "Pickeada",
  packed: "Empacada",
  labeled: "Etiquetada",
  dispatched: "Despachada",
  delivered: "Entregada",
  cancelled: "Cancelada",
  returned: "Devuelta",
  failed: "Fallida",
};

// El color dice qué pide la fila, no qué tan avanzada está: ámbar es "te toca a
// ti", azul es "en curso", verde es "listo" y gris es "acá no hay nada que
// hacer". Por eso Despachada y Entregada comparten el verde aunque sean etapas
// distintas: para la bodega las dos significan lo mismo.
export const WMS_ORDER_STATE_COLORS = {
  entered: "warning",
  reserved: "warning",
  picking: "info",
  picked: "info",
  packed: "info",
  labeled: "info",
  dispatched: "success",
  delivered: "success",
  cancelled: "default",
  returned: "error",
  failed: "error",
};

// De dónde llegó la orden. El backend las rotula con el nombre técnico del
// canal; acá se dicen como las nombra quien opera.
export const ORDER_SOURCE_LABELS = {
  excel: "Planilla",
  single_form_upload: "Manual",
  shopify: "Shopify",
  vtex: "VTEX",
  prestashop: "PrestaShop",
  meli: "Mercado Libre",
  meli_flex: "Mercado Libre Flex",
  meli_places: "Mercado Libre Places",
  meli_collect: "Mercado Libre Colecta",
  meli_full: "Mercado Libre Full",
  woocommerce: "WooCommerce",
  bootic: "Bootic",
  api: "API",
  not_selected: "Sin canal",
};

// Por qué el módulo no está activo para una empresa en una bodega. Son tres
// cosas distintas que se arreglan en tres lugares distintos, y decir solo "no"
// manda a la gente a mirar el equivocado.
export const MODULE_INACTIVE_LABELS = {
  contract_missing: "no tiene contrato activo en esta bodega",
  contract_off: "su contrato no tiene el módulo encendido",
  ceiling_off: "el titular de la bodega no tiene el módulo en su plan",
};

export const MODULE_INACTIVE_HINTS = {
  contract_missing: "El alta del contrato la hace NinjaHubs.",
  contract_off: "Se enciende en el contrato, desde el admin de Django.",
  ceiling_off:
    "En un centro de distribución es el plan del operador; en una bodega propia, el plan de la misma empresa. Nada se enciende sin que alguien lo haya comprado.",
};

export const QUOTA_UNIT_LABELS = {
  position: "Posición de pallet",
  m2: "Metro cuadrado",
  unit_month: "Unidad-mes",
};

/**
 * El subtítulo de una bodega en un selector.
 *
 * Qué conviene decir cambia con el tipo, porque cambia la pregunta que la
 * persona tiene en la cabeza:
 *
 *   centro de distribución   cuántas empresas operan adentro. El número ES la
 *                            información; puede haber tres o treinta.
 *   bodega del cliente       de quién es. Que haya "un cliente" es obvio por
 *                            definición, así que contarlo es ruido.
 *
 * En los dos casos, que no haya contrato activo se dice, porque una bodega
 * inoperable se ve idéntica a una operable hasta que alguien intenta usarla.
 */
export function warehouseSubtitle(warehouse, { showClients = true } = {}) {
  if (!warehouse) return "";
  const active = warehouse.clients?.length || 0;

  if (warehouse.kind === "distribution_center") {
    // `showClients` es falso cuando quien mira es el cliente. Para él, cuántas
    // empresas conviven en el centro no es información útil —y el servidor solo
    // le manda la suya, así que el número diría siempre "1 cliente", que además
    // de inútil confunde.
    if (!showClients) return "Centro de distribución";
    return active
      ? `Centro de distribución · ${active} cliente${active > 1 ? "s" : ""}`
      : "Centro de distribución · sin clientes activos";
  }

  const owner = warehouse.company_name
    ? `Bodega de ${warehouse.company_name}`
    : "Bodega del cliente";
  return active ? owner : `${owner} · sin contrato activo`;
}

/** true si en esta bodega no se puede operar con nadie. */
export const warehouseIsIdle = (warehouse) => !warehouse?.clients?.length;

export const stockStateLabel = (value) => STOCK_STATE_LABELS[value] || value;
export const movementTypeLabel = (value) => MOVEMENT_TYPE_LABELS[value] || value;

// De dónde viene un movimiento. Si lo movió una orden, quien mira el kardex
// reconoce su pedido por la referencia del e-commerce (`order_reference`), no
// por el id interno de Ninja; sin referencia queda "Orden #id". La clave técnica
// (`order:123`, `delivery:…:withdrawn`) no se pinta: es la del servidor.
export const movementReferenceLabel = (row) => {
  if (!row) return "";
  if (row.order_reference) return row.order_reference;
  if (row.order) return `Orden #${row.order}`;
  return row.reference || "";
};

// La línea chica bajo el tipo de movimiento: estado y origen.
export const movementDetailLabel = (row) =>
  [stockStateLabel(row.state), movementReferenceLabel(row)].filter(Boolean).join(" · ");
export const warehouseKindLabel = (value) => WAREHOUSE_KIND_LABELS[value] || value;
export const zoneTypeLabel = (value) => ZONE_TYPE_LABELS[value] || value;
export const receiptStatusLabel = (value) => RECEIPT_STATUS_LABELS[value] || value;
export const receiptEventLabel = (value) => RECEIPT_EVENT_LABELS[value] || value;
export const discrepancyReasonLabel = (value) =>
  DISCREPANCY_REASON_LABELS[value] || value;
export const quotaUnitLabel = (value) => QUOTA_UNIT_LABELS[value] || value;
export const moduleInactiveLabel = (value) => MODULE_INACTIVE_LABELS[value] || "no está activo";
export const moduleInactiveHint = (value) => MODULE_INACTIVE_HINTS[value] || "";
export const wmsOrderStateLabel = (value) => WMS_ORDER_STATE_LABELS[value] || value;
export const wmsOrderStateColor = (value) => WMS_ORDER_STATE_COLORS[value] || "default";
export const orderSourceLabel = (value) => ORDER_SOURCE_LABELS[value] || value || "—";

/**
 * Las cantidades llegan como número desde DRF (coerce_to_string=False) con
 * hasta tres decimales. Se muestran sin los ceros que no dicen nada: 12 y no
 * 12,000, pero 0,5 sí conserva su medio.
 */
export function formatQuantity(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return "—";
  return number.toLocaleString("es-CL", { maximumFractionDigits: 3 });
}
